import { Directive, inject, input, TemplateRef } from '@angular/core';

/**
 * The context a column filter template is rendered with: `let-column` is the column key,
 * `let-inDialog="inDialog"` tells the dialog (roomier controls) from the table's filter row.
 */
export interface FilterDefContext {
  $implicit: string;
  inDialog: boolean;
}

/**
 * A column's filter control, declared once inside `fr8bot-ui-list-filters` and rendered in two
 * places: the table's filter row on desktop and, labelled, the filters dialog on smaller screens.
 *
 * ```html
 * <ng-template fr8botUiFilterDef="id" [fr8botUiFilterDefLabel]="'load.list.table.id' | translate" let-column>
 *   <fr8bot-ui-input-field [formField]="filtersForm.loadNumber" size="small" />
 * </ng-template>
 * ```
 */
@Directive({ selector: 'ng-template[fr8botUiFilterDef]' })
export class FilterDefUIDirective {
  readonly key = input.required<string>({ alias: 'fr8botUiFilterDef' });
  /** The label above the control in the dialog — usually the column's header text. */
  readonly label = input('', { alias: 'fr8botUiFilterDefLabel' });

  readonly template = inject<TemplateRef<FilterDefContext>>(TemplateRef);

  static ngTemplateContextGuard(
    directive: FilterDefUIDirective,
    context: unknown,
  ): context is FilterDefContext {
    return context !== null;
  }
}

/** Table settings — status lines and columns: the toolbar's right side, the dialog's top. */
@Directive({ selector: 'ng-template[fr8botUiListFiltersSettings]' })
export class ListFiltersSettingsUIDirective {
  readonly template = inject(TemplateRef);
}

/** The context of the search and Smart Filter templates: `let-inDialog="inDialog"`. */
export interface ListFiltersContext {
  inDialog: boolean;
}

/**
 * The search field alone: in the toolbar on desktop; from 1024px down full width on the page
 * (`inDialog: true`), never inside the dialog, so it stays one tap away.
 */
@Directive({ selector: 'ng-template[fr8botUiListFiltersSearch]' })
export class ListFiltersSearchUIDirective {
  readonly template = inject<TemplateRef<ListFiltersContext>>(TemplateRef);

  static ngTemplateContextGuard(
    directive: ListFiltersSearchUIDirective,
    context: unknown,
  ): context is ListFiltersContext {
    return context !== null;
  }
}

/**
 * Filters that sit right after the search on desktop without a Smart Filter panel — a date
 * range, a pair of chips. From 1024px down they move into the dialog, under the settings.
 */
@Directive({ selector: 'ng-template[fr8botUiListFiltersQuick]' })
export class ListFiltersQuickUIDirective {
  readonly template = inject<TemplateRef<ListFiltersContext>>(TemplateRef);

  static ngTemplateContextGuard(
    directive: ListFiltersQuickUIDirective,
    context: unknown,
  ): context is ListFiltersContext {
    return context !== null;
  }
}

/**
 * The Smart Filter panel's fields — status chips, a board's ranges. In the dialog they get the
 * full width: chips in two columns (`fr8bot-ui-smart-filter-options-field` `layout="grid"`).
 */
@Directive({ selector: 'ng-template[fr8botUiListFiltersSmart]' })
export class ListFiltersSmartUIDirective {
  readonly template = inject<TemplateRef<ListFiltersContext>>(TemplateRef);

  static ngTemplateContextGuard(
    directive: ListFiltersSmartUIDirective,
    context: unknown,
  ): context is ListFiltersContext {
    return context !== null;
  }
}

/** The label in front of the Smart Filter fields (an icon and «Status»). */
@Directive({ selector: 'ng-template[fr8botUiListFiltersSmartLabel]' })
export class ListFiltersSmartLabelUIDirective {
  readonly template = inject(TemplateRef);
}
